import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="image-upload"
export default class extends Controller {
  static targets = ["input", "error", "warning", "preview"]
  static values = {
    maxSize: { type: Number, default: 30 * 1024 * 1024 }, // 30 MB
    compressionThreshold: { type: Number, default: 2 * 1024 * 1024 }, // 2 MB
    acceptedFormats: { 
      type: Array, 
      default: [
        "image/jpeg", "image/jpg", "image/png", "image/webp",
        "image/tiff", "image/x-tiff", "application/dicom"
      ] 
    },
    acceptedExtensions: {
      type: Array,
      default: [".jpg", ".jpeg", ".png", ".webp", ".tif", ".tiff", ".dcm", ".dicom"]
    }
  }

  connect() {
    console.log("Image upload controller connected")

    // Add change event listener to file inputs
    this.inputTargets.forEach(input => {
      input.addEventListener('change', (event) => this.validateFile(event))
    })
  }

  disconnect() {
    // Clean up event listeners
    this.inputTargets.forEach(input => {
      input.removeEventListener('change', this.validateFile)
    })
  }

  validateFile(event) {
    const input = event.target
    const file = input.files[0]

    // Find corresponding error and warning containers
    const errorContainer = this.findErrorContainer(input)
    const warningContainer = this.findWarningContainer(input)

    // Clear previous messages
    this.clearMessages(errorContainer, warningContainer)

    if (!file) {
      // Reset label to default text
      this.updateFileLabel(input, 'Choose file')
      return
    }

    // Update the label with the filename
    this.updateFileLabel(input, file.name)

    // Validate file
    const validation = this.validateFileObject(file)

    if (!validation.valid) {
      // Show error
      this.showError(errorContainer, validation.errors.join('<br>'))
      // Clear the input
      input.value = ''
      // Reset label to default text
      this.updateFileLabel(input, 'Choose file')
      return
    }

    // Show warnings if any
    if (validation.warnings.length > 0) {
      this.showWarning(warningContainer, validation.warnings.join('<br>'))
    }

    // Show preview if applicable
    this.showPreview(input, file)

    // Trigger autosave after file is validated and preview is shown
    this.triggerAutosave(input)
  }

  validateFileObject(file) {
    const errors = []
    const warnings = []

    // Get file extension
    const extension = file.name ? '.' + file.name.split('.').pop().toLowerCase() : ''
    
    // Validate format - check MIME type first, then extension as fallback
    const isValidMime = this.acceptedFormatsValue.includes(file.type)
    const isValidExtension = this.acceptedExtensionsValue.includes(extension)
    const isOctetStream = file.type === 'application/octet-stream'
    
    if (!isValidMime && !(isOctetStream && isValidExtension) && !isValidExtension) {
      errors.push(`Invalid format: ${file.type || 'unknown'}. Accepted: JPEG, PNG, WebP, TIFF, DICOM`)
    }
    
    // Add conversion warning for TIFF/DICOM
    if ((isValidMime || isValidExtension) && errors.length === 0) {
      if (extension.match(/\.(tif|tiff|dcm|dicom)$/i) || 
          file.type === 'image/tiff' || 
          file.type === 'image/x-tiff' || 
          file.type === 'application/dicom') {
        const formatName = extension.match(/\.(tif|tiff)$/i) ? 'TIFF' : 'DICOM'
        warnings.push(`${formatName} file will be converted to JPEG`)
      }
    }

    // Validate size
    if (file.size > this.maxSizeValue) {
      const maxSizeMB = (this.maxSizeValue / (1024 * 1024)).toFixed(0)
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1)
      errors.push(`File too large: ${fileSizeMB} MB (max ${maxSizeMB} MB)`)
    } else if (file.size > this.compressionThresholdValue) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1)
      warnings.push(`Image will be compressed (${fileSizeMB} MB)`)
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    }
  }

  needsConversionPlaceholder(file) {
    const extension = file.name ? '.' + file.name.split('.').pop().toLowerCase() : ''
    return extension.match(/\.(tif|tiff|dcm|dicom)$/i) ||
           file.type === 'image/tiff' ||
           file.type === 'image/x-tiff' ||
           file.type === 'application/dicom'
  }

  findErrorContainer(input) {
    // Look for error container sibling or parent
    let container = input.parentElement.querySelector('[data-image-upload-target="error"]')
    if (!container) {
      // Try searching in the column container
      const columnContainer = input.closest('.col-md-4, .col-sm-6, .form-group')
      container = columnContainer?.querySelector('[data-image-upload-target="error"]')
    }
    return container
  }

  findWarningContainer(input) {
    // Look for warning container sibling or parent
    let container = input.parentElement.querySelector('[data-image-upload-target="warning"]')
    if (!container) {
      // Try searching in the column container
      const columnContainer = input.closest('.col-md-4, .col-sm-6, .form-group')
      container = columnContainer?.querySelector('[data-image-upload-target="warning"]')
    }
    return container
  }

  clearMessages(errorContainer, warningContainer) {
    if (errorContainer) {
      errorContainer.innerHTML = ''
      errorContainer.classList.add('d-none')
    }
    if (warningContainer) {
      warningContainer.innerHTML = ''
      warningContainer.classList.add('d-none')
    }
  }

  showError(container, message) {
    if (!container) return

    container.innerHTML = `<div class="alert alert-danger alert-sm mb-2" role="alert">
      <i class="fas fa-exclamation-circle"></i> ${message}
    </div>`
    container.classList.remove('d-none')
  }

  showWarning(container, message) {
    if (!container) return

    container.innerHTML = `<div class="alert alert-warning alert-sm mb-2" role="alert">
      <i class="fas fa-info-circle"></i> ${message}
    </div>`
    container.classList.remove('d-none')
  }

  showPreview(input, file) {
    const previewContainer = this.findPreviewContainer(input)
    if (!previewContainer) {
      console.warn('Preview container not found for input:', input)
      return
    }

    // Check if format needs placeholder (can't preview client-side)
    if (this.needsConversionPlaceholder(file)) {
      const formatName = file.name.match(/\.(tif|tiff)$/i) ? 'TIFF' : 'DICOM'
      const fieldName = this.getFieldName(input)

      previewContainer.innerHTML = `
        <div class="position-relative d-inline-block text-center p-3 border rounded bg-light" style="min-width: 200px;">
          <i class="fa fa-file-image-o fa-3x text-muted mb-2"></i>
          <div class="small text-muted">${file.name}</div>
          <div class="badge badge-info mt-2">Will convert to JPEG</div>
          <button type="button" class="btn btn-danger btn-sm position-absolute" style="top: -10px; right: -10px;" data-action="click->image-upload#removeImage" data-field-name="${fieldName}">
            <i class="fa fa-times"></i>
          </button>
        </div>`
      previewContainer.classList.remove('d-none')

      // Store field name for later - we'll dispatch the event AFTER save completes
      this.pendingDicomField = fieldName
      this.pendingDicomInput = input

      return  // Skip standard FileReader preview
    }

    // Only show preview for images
    if (!file.type.startsWith('image/')) return

    const reader = new FileReader()
    reader.onload = (e) => {
      // Replace entire content with new preview - constrained size with remove button
      previewContainer.innerHTML = `
        <div class="position-relative d-inline-block">
          <img src="${e.target.result}" style="max-width: 200px; max-height: 150px; object-fit: contain;" class="img-thumbnail" alt="Preview">
          <button type="button" class="btn btn-danger btn-sm position-absolute" style="top: -10px; right: -10px;" data-action="click->image-upload#removeImage" data-field-name="${this.getFieldName(input)}">
            <i class="fa fa-times"></i>
          </button>
        </div>`
      previewContainer.classList.remove('d-none')
    }
    reader.readAsDataURL(file)
  }

  getFieldName(input) {
    // Extract field name from input name like "report[calibration_screenshot]" -> "calibration_screenshot"
    const match = input.name.match(/report\[(\w+)\]/)
    return match ? match[1] : ''
  }

  findPreviewContainer(input) {
    let container = input.parentElement.querySelector('[data-image-upload-target="preview"]')
    if (!container) {
      // Try searching in the column container
      const columnContainer = input.closest('.col-md-4, .col-sm-6, .form-group')
      container = columnContainer?.querySelector('[data-image-upload-target="preview"]')
    }
    return container
  }

  updateFileLabel(input, text) {
    // Find the label associated with this input
    const label = input.parentElement.querySelector('.custom-file-label')
    if (label) {
      label.textContent = text
    }
  }

  removeImage(event) {
    event.preventDefault()
    const button = event.currentTarget
    const fieldName = button.dataset.fieldName

    if (!fieldName) {
      console.error('No field name specified for remove button')
      return
    }

    console.log(`[image-upload] Removing ${fieldName}`)

    // Find the container (column or form-group)
    const columnContainer = button.closest('.col-md-4, .col-sm-6, .form-group[data-controller="image-upload"]')
    if (!columnContainer) return

    // Clear the preview
    const previewContainer = columnContainer.querySelector('[data-image-upload-target="preview"]')
    if (previewContainer) {
      previewContainer.innerHTML = '<div class="text-muted small">No image</div>'
    }

    // Clear the file input
    const fileInput = columnContainer.querySelector('input[type="file"]')
    if (fileInput) {
      fileInput.value = ''
      this.updateFileLabel(fileInput, 'Choose file')
    }

    // Clear the caption field
    // Caption fields are named like caption_one, caption_two, caption_three
    const captionFieldName = fieldName.replace('picture_', 'caption_')
    const captionField = columnContainer.querySelector(`input[name="report[${captionFieldName}]"]`)
    if (captionField) {
      console.log(`[image-upload] Clearing ${captionFieldName}`)
      captionField.value = ''
    }

    // Find or create a hidden field to mark the attachment for deletion
    let removeField = columnContainer.querySelector(`input[name="report[remove_${fieldName}]"]`)
    if (!removeField) {
      removeField = document.createElement('input')
      removeField.type = 'hidden'
      removeField.name = `report[remove_${fieldName}]`
      removeField.dataset.autosaveParam = 'true' // Mark as autosave parameter
      columnContainer.appendChild(removeField)
      console.log(`[image-upload] Created hidden field for remove_${fieldName}`)
    }
    removeField.value = '1'

    // Hide the remove button since there's no image anymore
    button.classList.add('d-none')

    // Trigger autosave if available
    const form = button.closest('form')
    if (form) {
      console.log('[image-upload] Triggering autosave after image removal')
      // Use a small delay to ensure the hidden field is in the DOM
      setTimeout(() => {
        form.dispatchEvent(new Event('change', { bubbles: true }))
      }, 100)
    }
  }

  // Manual validation trigger (can be called from form submit)
  validateAll() {
    let allValid = true

    this.inputTargets.forEach(input => {
      const file = input.files[0]
      if (!file) return

      const validation = this.validateFileObject(file)
      if (!validation.valid) {
        allValid = false
        const errorContainer = this.findErrorContainer(input)
        this.showError(errorContainer, validation.errors.join('<br>'))
      }
    })

    return allValid
  }

  // Trigger autosave event on the form
  triggerAutosave(input) {
    const form = input.closest('form')
    if (!form) {
      console.warn('[image-upload] No form found for autosave trigger')
      return
    }

    console.log('[image-upload] Triggering autosave after image selection')

    // Check if we have a pending DICOM upload that needs immediate save
    if (this.pendingDicomField) {
      console.log(`[image-upload] DICOM file pending - performing immediate save for ${this.pendingDicomField}`)
      this.performImmediateSave(form, input)
    } else {
      // For regular images, just dispatch change event for normal autosave
      setTimeout(() => {
        form.dispatchEvent(new Event('change', { bubbles: true }))
      }, 100)
    }
  }

  // Perform immediate save via AJAX and dispatch event when complete
  async performImmediateSave(form, input) {
    const fieldName = this.pendingDicomField

    // Find autosave URL - it's on the tabs container, not the form
    const tabsContainer = document.querySelector('[data-autosave-url-value]')
    const autosaveUrl = tabsContainer?.dataset.autosaveUrlValue ||
                        form.dataset.autosaveUrl ||
                        form.action.replace(/\/edit.*$/, '/autosave')

    console.log(`[image-upload] Performing immediate save to ${autosaveUrl}`)

    // Show saving indicator in preview
    const previewContainer = this.findPreviewContainer(input)
    if (previewContainer) {
      const badge = previewContainer.querySelector('.badge')
      if (badge) {
        badge.textContent = 'Saving...'
        badge.classList.remove('badge-info')
        badge.classList.add('badge-warning')
      }
    }

    try {
      // Create FormData from the form
      const formData = new FormData(form)

      // Get CSRF token
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch(autosaveUrl, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': csrfToken,
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        body: formData
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const data = await response.json()
      console.log('[image-upload] Immediate save successful:', data)

      // Update badge to show converting
      if (previewContainer) {
        const badge = previewContainer.querySelector('.badge')
        if (badge) {
          badge.textContent = 'Converting...'
          badge.classList.remove('badge-warning')
          badge.classList.add('badge-primary')
        }
      }

      // Now dispatch the dicom-upload event - file is saved, conversion started
      console.log(`[image-upload] Dispatching DICOM upload event for ${fieldName}`)
      this.dispatch('dicom-upload', { detail: { fieldName } })

      // Clear pending state
      this.pendingDicomField = null
      this.pendingDicomInput = null

    } catch (error) {
      console.error('[image-upload] Immediate save failed:', error)

      // Show error in preview
      if (previewContainer) {
        const badge = previewContainer.querySelector('.badge')
        if (badge) {
          badge.textContent = 'Save failed - try again'
          badge.classList.remove('badge-warning', 'badge-info')
          badge.classList.add('badge-danger')
        }
      }

      // Clear pending state
      this.pendingDicomField = null
      this.pendingDicomInput = null
    }
  }
}
